import { ArcSwingEffect } from '../skillVisualEffects/ArcSwingEffect';
import { ChainLightningEffect } from '../skillVisualEffects/ChainLightningEffect';
import { ExplosionEffect } from '../skillVisualEffects/ExplosionEffect';
import { LineShotEffect } from '../skillVisualEffects/LineShotEffect';
import { RingPulseEffect } from '../skillVisualEffects/RingPulseEffect';
import { SmiteEffect } from '../skillVisualEffects/SmiteEffect';
import type { PixelPosition, VisualEffect } from '../tilemap/types';
import { resolveBouncePositions, withParsedPayload } from './animationHandlers';
import type {
  ChainAnimationPayload,
  CleaveAnimationPayload,
  ExplosionAnimationPayload,
  SmiteAnimationPayload,
  SnipeAnimationPayload,
} from './types';

interface SharedMapAnimationContext {
  animationName: string;
  payloadJson: string;
  tileSize: number;
  tileToScreenCenter: (x: number, y: number) => PixelPosition;
  resolveEntityPosition: (entityId: string) => { x: number; y: number } | null;
  addEffect: (effect: VisualEffect) => void;
}

export function playSharedMapAnimation(context: SharedMapAnimationContext): boolean {
  const {
    animationName,
    payloadJson,
    tileSize,
    tileToScreenCenter,
    resolveEntityPosition,
    addEffect,
  } = context;

  if (animationName === 'chain_lightning' || animationName === 'lightning_wall') {
    withParsedPayload<ChainAnimationPayload>(animationName, payloadJson, (payload) => {
      const positions = resolveBouncePositions(animationName, payload, resolveEntityPosition);
      if (!positions || positions.length < 2) {
        return;
      }

      addEffect(new ChainLightningEffect({
        bounces: positions,
        tileToScreenCenter,
        durationMs:
          animationName === 'lightning_wall'
            ? Math.max(800, positions.length * 150 + 500)
            : undefined,
      }));
    });
    return true;
  }

  if (animationName === 'explosion' || animationName === 'scatter_shot') {
    withParsedPayload<ExplosionAnimationPayload>(animationName, payloadJson, (payload) => {
      const { x, y, radius } = payload;
      if (typeof x !== 'number' || typeof y !== 'number') {
        return;
      }
      addEffect(new ExplosionEffect({
        x,
        y,
        radius:
          typeof radius === 'number' && Number.isFinite(radius) && radius >= 0
            ? radius
            : 1,
        tileToScreenCenter,
        tileSize,
      }));
    });
    return true;
  }

  if (animationName === 'lightning_bolt') {
    withParsedPayload<SmiteAnimationPayload>(animationName, payloadJson, (payload) => {
      const { x, y } = payload;
      if (typeof x !== 'number' || typeof y !== 'number') {
        return;
      }
      addEffect(new SmiteEffect({
        target: { x, y },
        tileToScreenCenter,
        tileSize,
      }));
    });
    return true;
  }

  if (animationName === 'cleave') {
    withParsedPayload<CleaveAnimationPayload>(animationName, payloadJson, (payload) => {
      const { x, y, rotation, sweepDegrees } = payload;
      if (typeof x !== 'number' || typeof y !== 'number' || typeof rotation !== 'number') {
        return;
      }
      addEffect(new ArcSwingEffect({
        centerX: x,
        centerY: y,
        rotation,
        sweepDegrees: typeof sweepDegrees === 'number' ? sweepDegrees : 120,
        tileToScreenCenter,
        tileSize,
      }));
    });
    return true;
  }

  if (animationName === 'guard_stance' || animationName === 'berserker_fury') {
    withParsedPayload<SmiteAnimationPayload>(animationName, payloadJson, (payload) => {
      const { x, y } = payload;
      if (typeof x !== 'number' || typeof y !== 'number') {
        return;
      }
      addEffect(new RingPulseEffect({
        x,
        y,
        tileToScreenCenter,
        tileSize,
        color:
          animationName === 'guard_stance'
            ? 'rgba(147, 197, 253, 0.9)'
            : 'rgba(248, 113, 113, 0.95)',
      }));
    });
    return true;
  }

  if (animationName === 'snipe') {
    withParsedPayload<SnipeAnimationPayload>(animationName, payloadJson, (payload) => {
      const { fromX, fromY, toX, toY } = payload;
      if (![fromX, fromY, toX, toY].every((value) => typeof value === 'number')) {
        return;
      }
      addEffect(new LineShotEffect({
        fromX: fromX as number,
        fromY: fromY as number,
        toX: toX as number,
        toY: toY as number,
        tileToScreenCenter,
        tileSize,
        glowColor: 'rgba(255, 248, 196, 0.25)',
        coreColor: 'rgba(255, 214, 102, 0.95)',
      }));
    });
    return true;
  }

  return false;
}
